// @LTG-ID: data/brief-02
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: 08_FEATURES.md#F-051, source `Journals/sde-vs-ebitda.html`
// @CHANGED: 2026-02 session_004 (CHG-0062)
// @OWNER: web
//
// Non-React supporting data for Brief 02. React body lives inline in
// `pages/Brief02.jsx` per ORCH-0050 A4 (JSX-inline content, no MDX).

export const BRIEF_02_SLUG = "sde-vs-ebitda";

export const BRIEF_02_FRONTMATTER = {
    number: "02",
    section: "Core Valuation",
    sectionHref: "/journals",
    sectionAnchor: "core-valuation",
    title:
        "SDE vs EBITDA: which multiple actually applies to your business.",
    deck: "Two owners with identical tax returns can walk away from a sale with very different proceeds. The reason almost always sits in the earnings line. This is where the conversation flips.",
    author: "Sara F. Gonzalez, CPA",
    authorRole: "Managing Partner, KGOB",
    authorInitials: "SG",
    publishedISO: "2026-05-26",
    publishedHuman: "May 26, 2026",
    readMinutes: 12,
    lastReviewed: "2026-08-27",
    ctaId: "brief-02-order",
    ctaLabel: "Get Your Valuation",
    pageTitle:
        "SDE vs EBITDA: which multiple applies to your business · LTG",
    canonical: "https://letstalkgrowth.com/journals/sde-vs-ebitda",
    jsonLd: {
        "@context": "https://schema.org",
        "@type": "Article",
        headline:
            "SDE vs EBITDA: which multiple actually applies to your business",
        author: {
            "@type": "Person",
            name: "Sara F. Gonzalez",
            jobTitle: "Managing Partner, CPA",
        },
        publisher: {
            "@type": "Organization",
            name: "KGOB CPAs & Advisors",
        },
        datePublished: "2026-05-26",
        dateModified: "2026-05-26",
    },
};

export const BRIEF_02_AT_A_GLANCE = [
    {
        dt: "Target reader",
        dd: "Owner of a $500K to $5M revenue business preparing to sell within 24 months.",
    },
    {
        dt: "Key statistic 1",
        dd: "Businesses under <strong>$1M</strong> in earnings trade on SDE; deals above <strong>$2M</strong> trade on EBITDA. The transition zone in between is where the buyer pool, the financing, and the multiple structurally change (IBBA Market Pulse Q4 2025).",
    },
    {
        dt: "Key statistic 2",
        dd: "Recast EBITDA is the most-used multiples method in private-business appraisals, applied in <strong>76%</strong> of valuation engagements; guideline-company transactions carry the largest method weight at <strong>33%</strong> (Pepperdine PCM 2025).",
    },
    {
        dt: "Key statistic 3",
        dd: "Median 2025 multiples on $5M to $50M EBITDA deals averaged <strong>6.0x</strong> in Q4 2024 and ticked up through 2025 (IBBA Market Pulse).",
    },
    {
        dt: "Last reviewed",
        dd: "March 2026. Refreshed quarterly off IBBA Market Pulse releases.",
    },
];

export const BRIEF_02_SOURCES = [
    {
        id: "s1",
        html: "IBBA &amp; M&amp;A Source Market Pulse Report, Q4 2025. Available at ibba.org.",
    },
    {
        id: "s2",
        html: "Pepperdine Private Capital Markets Report 2025, Dr. Craig R. Everett, Pepperdine Graziadio Business School. Available at digitalcommons.pepperdine.edu.",
    },
    { id: "s3", html: "NACVA Glossary of Business Valuation Terms (2024)." },
    { id: "s4", html: "AICPA Forensic and Valuation Services Section." },
    {
        id: "s5",
        html: "CT Acquisitions, Manufacturing Industry Valuation Report, 2026.",
    },
    {
        id: "s6",
        html: "Profitability Partners, Home Services M&amp;A Report 2025.",
    },
];

export const BRIEF_02_END_CTA = {
    label: "Your number, normalized",
    headlineLead: "Get the indicative valuation with a",
    headlineEm: "defensible",
    headlineTrail: " earnings calculation.",
    body:
        "The Strategic Snapshot applies the methodology in this article to your business: SDE or EBITDA as appropriate, documented add-backs, indicative range, and the next three priority moves.",
    secondaryHref: "/#snapshot",
    secondaryLabel: "Try the free preview →",
    microLine:
        "Indicative valuation, not a formal appraisal under SSVS No. 1",
};

export const BRIEF_02_READ_NEXT = [
    {
        tag: "Brief 01 · Core Valuation",
        title:
            "What is my business actually worth? An owner's primer.",
        meta: "14 min read · Published",
        to: "/journals/what-is-my-business-worth",
    },
    {
        tag: "Brief 03 · Core Valuation",
        title: "The three approaches to value: income, market, asset.",
        meta: "11 min read · Forthcoming",
        to: "/journals#brief-3",
    },
    {
        tag: "Brief 29 · Deal Structures",
        title:
            "Working capital pegs, escrows, and what reduces your \"true\" sale price.",
        meta: "13 min read · Forthcoming",
        to: "/journals#brief-29",
    },
];

// Accept / Reject add-back grid — Brief 02 only (source lines 343-368).
export const BRIEF_02_ACCEPT = [
    "One owner's compensation, replaced with a market-rate manager salary",
    "One-time professional fees (litigation, the sale process itself)",
    "Personal vehicles, travel, insurance, club memberships run through the business",
    "Family members on payroll who do not work in the business",
    "Pandemic-era one-time costs (PPE, temporary remote-work infrastructure)",
    "Documented one-time bad-debt write-offs from a since-resolved issue",
    "Owner's personal phone and home-office allocation",
];

export const BRIEF_02_REJECT = [
    "Aspirational cost savings (\"if I cut these contracts, EBITDA would be higher\")",
    "Revenue not yet booked (\"we are about to land a big customer\")",
    "Owner compensation in excess of the market-rate replacement",
    "The \"if you fired me\" calculation that assumes no replacement",
    "Recurring discretionary spending presented as one-time",
    "Owner perquisites the next operator will also expect",
    "Synergies that depend on the buyer's existing operations (those are the buyer's, not yours)",
];
